import { useCallback, useEffect, useRef, useState } from 'react';

type Cached<T> = { data: T; fetchedAt: number };

export function readCache<T>(key: string): Cached<T> | null {
    try {
        const raw = window.localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed.fetchedAt === 'number' && 'data' in parsed) {
            return parsed as Cached<T>;
        }
    } catch {
        /* ignore — corrupt cache, fall through to fetch */
    }
    return null;
}

export function writeCache<T>(key: string, data: T): void {
    try {
        window.localStorage.setItem(
            key,
            JSON.stringify({ data, fetchedAt: Date.now() })
        );
    } catch {
        /* localStorage full or disabled — drop silently */
    }
}

export function dropCache(key: string): void {
    try {
        window.localStorage.removeItem(key);
    } catch {
        /* ignore */
    }
}

export interface UseLocalCacheResult<T> {
    data: T | null;
    isStale: boolean;
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
}

/**
 * 5-minute stale-while-revalidate cache backed by `localStorage`.
 *
 * Hydrates synchronously from cache so the first render uses stale data,
 * then kicks off a background refresh. On 401/403 the cache entry is dropped
 * so a logged-out viewer doesn't keep seeing someone else's data.
 *
 * `refetch()` always bypasses the TTL gate — used by pull-to-refresh.
 */
export function useLocalCache<T>(
    key: string | null,
    ttlMs: number,
    fetcher: () => Promise<T>
): UseLocalCacheResult<T> {
    const [data, setData] = useState<T | null>(() => {
        if (!key) return null;
        return readCache<T>(key)?.data ?? null;
    });
    const [isStale, setIsStale] = useState<boolean>(() => {
        if (!key) return false;
        const c = readCache<T>(key);
        return !!c && Date.now() - c.fetchedAt > ttlMs;
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const reqSeq = useRef(0);
    const fetcherRef = useRef(fetcher);
    fetcherRef.current = fetcher;

    const run = useCallback(async () => {
        if (!key) return;
        const seq = ++reqSeq.current;
        setLoading(true);
        setError(null);
        try {
            const next = await fetcherRef.current();
            if (seq !== reqSeq.current) return;
            writeCache(key, next);
            setData(next);
            setIsStale(false);
        } catch (err: any) {
            if (seq !== reqSeq.current) return;
            const status = err?.response?.status;
            if (status === 401 || status === 403) {
                dropCache(key);
                setData(null);
            }
            setError(err?.response?.data?.error || err?.message || 'Request failed');
        } finally {
            if (seq === reqSeq.current) setLoading(false);
        }
    }, [key]);

    useEffect(() => {
        if (!key) return;
        const c = readCache<T>(key);
        if (c) {
            setData(c.data);
            setIsStale(Date.now() - c.fetchedAt > ttlMs);
        } else {
            setData(null);
            setIsStale(false);
        }
        void run();
    }, [key, ttlMs, run]);

    return { data, isStale, loading, error, refetch: run };
}
